import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Briefcase, Wallet, Calendar, ChevronLeft, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, API_BASE } from "@/lib/api";
import ShareButtons from "@/components/site/ShareButtons";
import ApplyForm from "@/components/site/ApplyForm";
import { useSEO } from "@/lib/seo";

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/${slug}`)
      .then((r) => setJob(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO({
    title: job ? (job.meta_title || job.title) : "Job",
    description: job ? (job.meta_description || job.description?.slice(0, 160)) : undefined,
    keywords: job?.meta_keywords || undefined,
    image: job?.og_image || undefined,
    type: "article",
    breadcrumbs: job ? [
      { name: "Home", url: "/" },
      { name: "Openings", url: "/jobs" },
      { name: job.title, url: `/jobs/${job.slug}` },
    ] : undefined,
    jsonLd: job ? {
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      title: job.title,
      description: job.description,
      datePosted: job.posted_at,
      validThrough: job.valid_through || undefined,
      employmentType: (job.employment_type || "FULL_TIME").toUpperCase().replace("-", "_"),
      hiringOrganization: { "@type": "Organization", name: "PharmacoHires", sameAs: typeof window !== "undefined" ? window.location.origin : "" },
      jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: job.location } },
      baseSalary: job.salary_range ? { "@type": "MonetaryAmount", currency: "INR", value: { "@type": "QuantitativeValue", value: job.salary_range } } : undefined,
    } : undefined,
  });

  if (loading) return <div className="container-page py-20 text-center text-slate-500">Loading…</div>;
  if (notFound || !job) return (
    <div className="container-page py-20 text-center">
      <h1 className="font-heading text-3xl font-semibold text-slate-900">Role not found</h1>
      <p className="mt-3 text-slate-600">It may have been filled or closed.</p>
      <Link to="/jobs" className="mt-6 inline-block"><Button>Back to openings</Button></Link>
    </div>
  );

  const exp = job.experience_min === job.experience_max
    ? `${job.experience_min} yrs`
    : `${job.experience_min}-${job.experience_max} yrs`;

  return (
    <>
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-page py-10">
          <Link to="/jobs" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary" data-testid="back-to-jobs">
            <ChevronLeft className="h-4 w-4" /> All openings
          </Link>
          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              {job.department && <div className="overline mb-2">{job.department}</div>}
              <h1 className="font-heading text-3xl font-bold text-slate-900 sm:text-4xl">{job.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-700">
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" />{job.location}</span>
                <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-primary" />{job.employment_type}</span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-primary" />{exp}</span>
                {job.salary_range && (
                  <span className="inline-flex items-center gap-1.5"><Wallet className="h-4 w-4 text-primary" />{job.salary_range}</span>
                )}
              </div>
            </div>
            <a href="#apply" data-testid="apply-jump">
              <Button size="lg" className="bg-primary hover:bg-primary/90"><Send className="mr-2 h-4 w-4" /> Apply Now</Button>
            </a>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-page grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="prose-pharma max-w-none">
              <h2 className="font-heading text-2xl font-semibold text-slate-900">About the role</h2>
              <p className="mt-3 whitespace-pre-line">{job.description}</p>

              {job.responsibilities?.length > 0 && (
                <>
                  <h3 className="font-heading mt-8 text-xl font-semibold text-slate-900">Key responsibilities</h3>
                  <ul className="mt-3 space-y-2">
                    {job.responsibilities.map((r, i) => (
                      <li key={i} className="flex items-start gap-2"><span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{r}</li>
                    ))}
                  </ul>
                </>
              )}

              {job.requirements?.length > 0 && (
                <>
                  <h3 className="font-heading mt-8 text-xl font-semibold text-slate-900">Requirements</h3>
                  <ul className="mt-3 space-y-2">
                    {job.requirements.map((r, i) => (
                      <li key={i} className="flex items-start gap-2"><span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-secondary" />{r}</li>
                    ))}
                  </ul>
                </>
              )}

              {job.skills?.length > 0 && (
                <>
                  <h3 className="font-heading mt-8 text-xl font-semibold text-slate-900">Skills</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {job.skills.map((s) => (
                      <Badge key={s} variant="secondary" className="bg-slate-100 text-slate-700">{s}</Badge>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="mt-10 border-t border-slate-200 pt-6">
              <ShareButtons url={`/jobs/${job.slug}`} title={job.title} />
            </div>
          </div>

          <div className="lg:col-span-4">
            <div id="apply" className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Apply for this role</h3>
              <p className="mt-1 text-sm text-slate-600">It takes under a minute.</p>
              <div className="mt-5">
                <ApplyForm jobSlug={job.slug} jobTitle={job.title} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
